'use client';

import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import { AlertCircle, ChevronLeft, ChevronRight, Gauge, Keyboard, LoaderCircle, Maximize, Minimize, Pause, PictureInPicture2, Play, Repeat2, RotateCcw, RotateCw, Settings, Volume1, Volume2, VolumeX, X } from 'lucide-react';
import { Checkmark } from './checkmark';
import { cn } from '@/lib/utils';
import styles from './video-player.module.css';

type VideoPlayerProps = {
  src: string;
  poster?: string;
  title?: string;
  className?: string;
  style?: CSSProperties;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  lazy?: boolean;
  pauseWhenHidden?: boolean;
};
type SafariVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void };
const SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
type SettingsPage = 'main' | 'speed' | 'shortcuts';
const SHORTCUTS = [
  ['Play / pause', 'Space / K'], ['Back / forward 5 seconds', '← / →'], ['Back / forward 10 seconds', 'J / L'],
  ['Volume', '↑ / ↓'], ['Mute', 'M'], ['Fullscreen', 'F'], ['Jump to 0–90%', '0–9'],
];

function clock(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds), hours = Math.floor(total / 3600), minutes = Math.floor(total / 60) % 60;
  return `${hours ? `${hours}:${String(minutes).padStart(2, '0')}` : minutes}:${String(total % 60).padStart(2, '0')}`;
}

/** Source changes reset media state, timers and lazy-loading as one unit. */
export function VideoPlayer(props: VideoPlayerProps) {
  return <Player key={props.src} {...props} />;
}

function Player({ src, poster, title = 'Video', className, style, autoPlay = false, muted = false, loop = false, lazy = false, pauseWhenHidden = false }: VideoPlayerProps) {
  const root = useRef<HTMLDivElement>(null), video = useRef<HTMLVideoElement>(null), settingsButton = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null), clickTimer = useRef<ReturnType<typeof setTimeout>>(undefined), hideTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastVolume = useRef(1), scrubbing = useRef(false), wasPlaying = useRef(false);
  const [active, setActive] = useState(!lazy || autoPlay), [playing, setPlaying] = useState(false), [ended, setEnded] = useState(false);
  const [current, setCurrent] = useState(0), [duration, setDuration] = useState(0), [buffered, setBuffered] = useState<Array<[number, number]>>([]);
  const [volume, setVolume] = useState(1), [silent, setSilent] = useState(muted), [speed, setSpeed] = useState(1);
  const [visible, setVisible] = useState(true), [hoveringControls, setHoveringControls] = useState(false), [keyboardFocus, setKeyboardFocus] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false), [fullscreen, setFullscreen] = useState(false), [pip, setPip] = useState(false), [pipSupported, setPipSupported] = useState(false);
  const [settingsPage, setSettingsPage] = useState<SettingsPage>('main'), [repeat, setRepeat] = useState(loop);
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>();
  const [waiting, setWaiting] = useState(false), [error, setError] = useState(''), [notice, setNotice] = useState('');
  const [hoverTime, setHoverTime] = useState<{ time: number; percent: number } | null>(null);
  const settingsId = useId(), settingsHeadingId = useId(), settingsVolumeId = useId(), helpId = useId();
  const controlsVisible = visible || !playing || settingsOpen || keyboardFocus || hoveringControls || Boolean(error);
  const progress = duration > 0 ? Math.min(100, current / duration * 100) : 0;

  useEffect(() => {
    clearTimeout(hideTimer.current);
    if (!playing || !visible || settingsOpen || keyboardFocus || hoveringControls) return;
    hideTimer.current = setTimeout(() => setVisible(false), 2600);
    return () => clearTimeout(hideTimer.current);
  }, [playing, visible, settingsOpen, keyboardFocus, hoveringControls]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(''), 1800);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    const element = video.current, container = root.current;
    if (!element || !container) return;
    const observers: IntersectionObserver[] = [];
    if (typeof IntersectionObserver !== 'undefined') {
      if (lazy) {
        const observer = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting) { setActive(true); observer.disconnect(); }
        }, { rootMargin: '200px' });
        observer.observe(container); observers.push(observer);
      }
      if (pauseWhenHidden) {
        const observer = new IntersectionObserver(entries => {
          if (!entries[0].isIntersecting && document.pictureInPictureElement !== element && document.fullscreenElement !== container) element.pause();
        });
        observer.observe(container); observers.push(observer);
      }
    }
    const visibility = () => { if (document.hidden && document.pictureInPictureElement !== element) element.pause(); };
    const fullscreenChange = () => setFullscreen(document.fullscreenElement === container);
    const pipChange = () => setPip(document.pictureInPictureElement === element);
    document.addEventListener('visibilitychange', visibility);
    document.addEventListener('fullscreenchange', fullscreenChange);
    element.addEventListener('enterpictureinpicture', pipChange);
    element.addEventListener('leavepictureinpicture', pipChange);
    return () => {
      observers.forEach(observer => observer.disconnect());
      document.removeEventListener('visibilitychange', visibility);
      document.removeEventListener('fullscreenchange', fullscreenChange);
      element.removeEventListener('enterpictureinpicture', pipChange);
      element.removeEventListener('leavepictureinpicture', pipChange);
      clearTimeout(clickTimer.current);
      clearTimeout(hideTimer.current);
      element.pause();
    };
  }, [lazy, pauseWhenHidden]);

  useEffect(() => {
    if (!settingsOpen) return;
    const initial = menu.current?.querySelector<HTMLButtonElement>(settingsPage === 'speed' ? '[role="radio"][aria-checked="true"]' : '[data-settings-initial]');
    initial?.focus({ preventScroll: true });
    if (settingsPage === 'speed') initial?.scrollIntoView({ block: 'nearest' });
    const outside = (event: globalThis.PointerEvent) => {
      if (event.target instanceof Node && !menu.current?.contains(event.target) && !settingsButton.current?.contains(event.target)) setSettingsOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [settingsOpen, settingsPage]);

  function reveal() {
    setVisible(true);
    clearTimeout(hideTimer.current);
    if (playing && !settingsOpen && !keyboardFocus && !hoveringControls) hideTimer.current = setTimeout(() => setVisible(false), 2600);
  }

  async function play() {
    const element = video.current;
    if (!element) return;
    setError('');
    // A click can activate a lazy video before its observer has run.
    if (!element.getAttribute('src')) { element.src = src; setActive(true); }
    try {
      if (element.ended) element.currentTime = 0;
      await element.play();
    } catch (failure) {
      if (failure instanceof DOMException && ['AbortError', 'NotAllowedError'].includes(failure.name)) return;
      setError('This video couldn’t be played. Try loading it again.');
      setWaiting(false);
    }
  }

  function togglePlay() {
    const element = video.current;
    if (!element) return;
    reveal();
    if (element.paused || element.ended) void play(); else element.pause();
  }

  function seek(value: number) {
    const element = video.current;
    if (!element || !Number.isFinite(element.duration) || element.duration <= 0) return;
    const next = Math.max(0, Math.min(element.duration, value));
    element.currentTime = next; setCurrent(next); setEnded(false); reveal();
  }

  function skip(seconds: number) {
    seek((video.current?.currentTime ?? 0) + seconds);
    setNotice(`${seconds < 0 ? '−' : '+'}${Math.abs(seconds)} seconds`);
  }

  function changeVolume(value: number) {
    const element = video.current;
    if (!element) return;
    element.volume = Math.max(0, Math.min(1, value)); element.muted = value === 0;
    if (value > 0) lastVolume.current = value;
  }

  function toggleMute() {
    const element = video.current;
    if (!element) return;
    if (element.muted || element.volume === 0) {
      element.muted = false;
      if (element.volume === 0) element.volume = lastVolume.current;
    } else element.muted = true;
  }

  async function toggleFullscreen() {
    const container = root.current, element = video.current as SafariVideo | null;
    if (!container || !element) return;
    try {
      if (document.fullscreenElement === container) await document.exitFullscreen();
      else if (container.requestFullscreen && document.fullscreenEnabled) await container.requestFullscreen();
      else if (element.webkitEnterFullscreen) element.webkitEnterFullscreen();
      else setNotice('Fullscreen is unavailable in this browser.');
    } catch { setNotice('Fullscreen couldn’t be opened. Please try again.'); }
  }

  async function togglePip() {
    const element = video.current;
    if (!element) return;
    try {
      if (document.pictureInPictureElement === element) await document.exitPictureInPicture();
      else await element.requestPictureInPicture();
    } catch { setNotice('Picture-in-picture is unavailable right now.'); }
  }

  function changeSpeed(value: number) {
    if (video.current) video.current.playbackRate = value;
    closeSettings();
  }

  function closeSettings() {
    setSettingsOpen(false); setSettingsPage('main'); settingsButton.current?.focus({ preventScroll: true }); reveal();
  }

  function resetSettings() {
    // Keep focus inside the panel before the reset button becomes disabled.
    menu.current?.querySelector<HTMLButtonElement>('[data-settings-initial]')?.focus({ preventScroll: true });
    if (video.current) video.current.playbackRate = 1;
    changeVolume(1); setRepeat(loop);
  }

  function syncMetadata() {
    const element = video.current;
    if (!element) return;
    setDuration(Number.isFinite(element.duration) ? element.duration : 0);
    if (element.videoWidth > 0 && element.videoHeight > 0) setDimensions({ width: element.videoWidth, height: element.videoHeight });
    setPipSupported(Boolean(document.pictureInPictureEnabled && typeof element.requestPictureInPicture === 'function'));
    syncBuffer();
  }

  function syncBuffer() {
    const element = video.current;
    if (!element || !Number.isFinite(element.duration) || element.duration <= 0) return;
    const ranges: Array<[number, number]> = [];
    for (let i = 0; i < element.buffered.length; i++) ranges.push([element.buffered.start(i) / element.duration * 100, element.buffered.end(i) / element.duration * 100]);
    setBuffered(ranges);
  }

  function hoverSeek(event: PointerEvent<HTMLDivElement>) {
    if (!duration || event.pointerType === 'touch') return;
    const rect = event.currentTarget.getBoundingClientRect(), fraction = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    setHoverTime({ time: fraction * duration, percent: fraction * 100 });
  }

  function finishScrub() {
    if (!scrubbing.current) return;
    scrubbing.current = false;
    if (wasPlaying.current) void play();
  }

  function keyDown(event: KeyboardEvent<HTMLDivElement>) {
    event.stopPropagation();
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    setKeyboardFocus(true); reveal();
    if (settingsOpen && (event.key === 'Escape' || event.key === 'ArrowLeft' && settingsPage !== 'main')) {
      event.preventDefault();
      if (settingsPage !== 'main') setSettingsPage('main'); else closeSettings();
      return;
    }
    if (event.target instanceof HTMLElement && event.target.closest('input, select, textarea, [contenteditable="true"]')) return;
    if (settingsOpen && ['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const buttons = Array.from(menu.current?.querySelectorAll<HTMLButtonElement>(settingsPage === 'speed' ? '[role="radio"]' : 'button:not(:disabled)') ?? []), index = buttons.indexOf(document.activeElement as HTMLButtonElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
      if (settingsPage === 'speed' && video.current) video.current.playbackRate = SPEEDS[next];
      buttons[next]?.focus();
      return;
    }
    if (settingsOpen || event.key === ' ' && event.target instanceof HTMLElement && event.target.closest('button')) return;
    const key = event.key.toLowerCase();
    if (![' ', 'k', 'j', 'l', 'm', 'f', 'i', 'arrowleft', 'arrowright', 'arrowup', 'arrowdown', 'home', 'end'].includes(key) && !/^\d$/.test(key)) return;
    event.preventDefault();
    if (key === ' ' || key === 'k') togglePlay();
    else if (key === 'j' || key === 'arrowleft') skip(key === 'j' ? -10 : -5);
    else if (key === 'l' || key === 'arrowright') skip(key === 'l' ? 10 : 5);
    else if (key === 'm') toggleMute();
    else if (key === 'f') void toggleFullscreen();
    else if (key === 'i' && pipSupported) void togglePip();
    else if (key === 'arrowup' || key === 'arrowdown') changeVolume((video.current?.volume ?? 1) + (key === 'arrowup' ? 0.05 : -0.05));
    else if (key === 'home') seek(0);
    else if (key === 'end') seek(duration);
    else if (/^\d$/.test(key)) seek(duration * Number(key) / 10);
  }

  const VolumeIcon = silent || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
  return <div ref={root} role="region" aria-label={title === 'Video' ? 'Video player' : `${title} video player`} aria-describedby={helpId} tabIndex={0} data-custom-video-player data-post-interactive
    data-controls-visible={controlsVisible} data-playing={playing} className={cn(styles.player, className)} style={style}
    onClick={event => event.stopPropagation()} onDoubleClick={event => event.stopPropagation()} onKeyDown={keyDown}
    onPointerMove={reveal} onPointerDown={() => { setKeyboardFocus(false); reveal(); }} onPointerLeave={() => { if (playing && !settingsOpen) setVisible(false); }}
    onFocusCapture={reveal} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) { setKeyboardFocus(false); setSettingsOpen(false); } }}>
    <p id={helpId} className={styles.srOnly}>Space or K: play or pause. J and L: skip 10 seconds. Arrow keys: seek or adjust volume. M: mute. F: fullscreen. Number keys: jump through the video.</p>
    <video ref={video} data-custom-video src={active ? src : undefined} poster={poster} preload="metadata" playsInline autoPlay={autoPlay} muted={muted} loop={repeat} aria-label={title} className={styles.video}
      onLoadedMetadata={syncMetadata} onDurationChange={syncMetadata} onProgress={syncBuffer}
      onTimeUpdate={event => { if (!scrubbing.current) setCurrent(event.currentTarget.currentTime); }}
      onPlay={event => {
        document.querySelectorAll<HTMLVideoElement>('video[data-custom-video]').forEach(other => { if (other !== event.currentTarget) other.pause(); });
        setPlaying(true); setEnded(false); setError(''); reveal();
      }} onPause={() => { setPlaying(false); setWaiting(false); }} onEnded={() => { setEnded(true); setPlaying(false); setWaiting(false); }}
      onWaiting={() => setWaiting(true)} onSeeking={() => setWaiting(true)} onPlaying={() => setWaiting(false)} onCanPlay={() => setWaiting(false)} onSeeked={() => setWaiting(false)}
      onVolumeChange={event => { setVolume(event.currentTarget.volume); setSilent(event.currentTarget.muted); }} onRateChange={event => setSpeed(event.currentTarget.playbackRate)}
      onError={() => { setError('This video couldn’t be loaded. Check your connection and try again.'); setWaiting(false); setPlaying(false); }} />
    <button type="button" tabIndex={-1} className={styles.surface} aria-label={playing ? 'Pause video' : ended ? 'Replay video' : 'Play video'}
      onClick={() => { clearTimeout(clickTimer.current); clickTimer.current = setTimeout(togglePlay, 200); }} />
    {title !== 'Video' && <div className={styles.title} aria-hidden="true">{title}</div>}
    {!playing && !error && <button type="button" className={styles.bigPlay} aria-label={ended ? 'Replay video' : 'Play video'} onClick={togglePlay}>{ended ? <RotateCcw /> : <Play className={styles.playIcon} />}</button>}
    {waiting && playing && <div role="status" className={styles.loading}><LoaderCircle /><span className={styles.srOnly}>Buffering video</span></div>}
    {error && <div role="alert" className={styles.error}><AlertCircle /><p>{error}</p><button type="button" onClick={() => { setError(''); video.current?.load(); void play(); }}><RotateCcw />Try again</button></div>}
    {notice && <div role="status" className={styles.notice}>{notice}</div>}
    {pip && <div className={styles.pipMessage}><PictureInPicture2 /><span>Playing in picture-in-picture</span></div>}
    <div className={styles.controls} onPointerEnter={() => setHoveringControls(true)} onPointerLeave={() => setHoveringControls(false)}>
      <div className={styles.seek} onPointerMove={hoverSeek} onPointerLeave={() => setHoverTime(null)}>
        <div className={styles.track} aria-hidden="true">{buffered.map(([start, end], i) => <span key={i} className={styles.buffer} style={{ left: `${start}%`, width: `${end - start}%` }} />)}<span className={styles.progress} style={{ width: `${progress}%` }} /><span className={styles.thumb} style={{ left: `${progress}%` }} /></div>
        <input type="range" min={0} max={duration || 0} step={0.1} value={Math.min(current, duration)} disabled={!duration || Boolean(error)} aria-label="Seek video" aria-valuetext={`${clock(current)} of ${clock(duration)}`} className={styles.seekInput}
          onChange={event => seek(Number(event.target.value))} onPointerDown={event => { scrubbing.current = true; wasPlaying.current = Boolean(video.current && !video.current.paused); video.current?.pause(); event.currentTarget.setPointerCapture(event.pointerId); }}
          onPointerUp={finishScrub} onPointerCancel={finishScrub} onLostPointerCapture={finishScrub} />
        {hoverTime && <span className={styles.seekPreview} style={{ left: `clamp(25px, ${hoverTime.percent}%, calc(100% - 25px))` }}>{clock(hoverTime.time)}</span>}
      </div>
      <div className={styles.toolbar}>
        <button type="button" className={styles.control} aria-label={playing ? 'Pause' : ended ? 'Replay' : 'Play'} title={playing ? 'Pause (k)' : 'Play (k)'} onClick={togglePlay}>{playing ? <Pause /> : ended ? <RotateCcw /> : <Play />}</button>
        <button type="button" className={cn(styles.control, styles.skip)} aria-label="Rewind 10 seconds" title="Rewind 10 seconds (j)" disabled={!duration} onClick={() => skip(-10)}><RotateCcw /><span>10</span></button>
        <button type="button" className={cn(styles.control, styles.skip)} aria-label="Forward 10 seconds" title="Forward 10 seconds (l)" disabled={!duration} onClick={() => skip(10)}><RotateCw /><span>10</span></button>
        <div className={styles.volumeGroup}><button type="button" className={styles.control} aria-label={silent || volume === 0 ? 'Unmute' : 'Mute'} title={silent || volume === 0 ? 'Unmute (m)' : 'Mute (m)'} onClick={toggleMute}><VolumeIcon /></button>
          <input type="range" min={0} max={1} step={0.05} value={silent ? 0 : volume} aria-label="Volume" aria-valuetext={`${Math.round((silent ? 0 : volume) * 100)}%`} className={styles.volume} style={{ '--volume': `${(silent ? 0 : volume) * 100}%` } as CSSProperties} onChange={event => changeVolume(Number(event.target.value))} />
        </div>
        <span className={styles.time} aria-hidden="true">{clock(current)}<span> / {clock(duration)}</span></span>
        <div className={styles.rightControls}>
          <button ref={settingsButton} type="button" className={cn(styles.control, styles.settingsButton)} aria-label="Playback settings" title="Playback settings" aria-haspopup="dialog" aria-expanded={settingsOpen} aria-controls={settingsId} onClick={() => { setSettingsPage('main'); setSettingsOpen(value => !value); }}><Settings />{speed !== 1 && <span>{speed}×</span>}</button>
          {pipSupported && <button type="button" className={cn(styles.control, styles.pip)} aria-label={pip ? 'Exit picture-in-picture' : 'Picture-in-picture'} title="Picture-in-picture (i)" onClick={() => void togglePip()}><PictureInPicture2 /></button>}
          <button type="button" className={styles.control} aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'} title={fullscreen ? 'Exit fullscreen (f)' : 'Fullscreen (f)'} onClick={() => void toggleFullscreen()}>{fullscreen ? <Minimize /> : <Maximize />}</button>
        </div>
      </div>
    </div>
    {settingsOpen && <div ref={menu} id={settingsId} role="dialog" aria-modal="false" aria-labelledby={settingsHeadingId} className={styles.settings}>
      <header className={styles.settingsHeading}>
        {settingsPage !== 'main' && <button type="button" data-settings-initial aria-label="Back to video settings" onClick={() => setSettingsPage('main')}><ChevronLeft /></button>}
        <h2 id={settingsHeadingId}>{settingsPage === 'speed' ? 'Playback speed' : settingsPage === 'shortcuts' ? 'Keyboard shortcuts' : 'Video settings'}</h2>
        <button type="button" aria-label="Close video settings" onClick={closeSettings}><X /></button>
      </header>
      <div className={styles.settingsBody}>
        {settingsPage === 'main' ? <>
          <button type="button" data-settings-initial className={styles.settingsRow} onClick={() => setSettingsPage('speed')}><Gauge /><span>Playback speed</span><span className={styles.settingValue}>{speed === 1 ? 'Normal' : `${speed}×`}</span><ChevronRight /></button>
          <button type="button" role="switch" aria-label="Loop video" aria-checked={repeat} className={styles.settingsRow} onClick={() => setRepeat(value => !value)}><Repeat2 /><span>Loop video</span><span aria-hidden="true" className={styles.toggle} data-checked={repeat}><span /></span></button>
          <div className={styles.audioSettings}>
            <div className={styles.audioHeading}><label htmlFor={settingsVolumeId}>Volume</label><span>{Math.round((silent ? 0 : volume) * 100)}%</span></div>
            <div className={styles.audioControls}><button type="button" aria-label={silent || volume === 0 ? 'Unmute audio' : 'Mute audio'} onClick={toggleMute}><VolumeIcon /></button><input id={settingsVolumeId} type="range" min={0} max={1} step={0.05} value={silent ? 0 : volume} aria-label="Video volume" aria-valuetext={`${Math.round((silent ? 0 : volume) * 100)}%`} className={styles.volume} style={{ '--volume': `${(silent ? 0 : volume) * 100}%` } as CSSProperties} onChange={event => changeVolume(Number(event.target.value))} /></div>
          </div>
          <button type="button" className={styles.settingsRow} onClick={() => setSettingsPage('shortcuts')}><Keyboard /><span>Keyboard shortcuts</span><ChevronRight /></button>
          {dimensions && <div className={styles.sourceInfo}><span>Original video</span><span>{dimensions.width} × {dimensions.height}</span></div>}
          <button type="button" className={styles.resetSettings} disabled={speed === 1 && volume === 1 && !silent && repeat === loop} onClick={resetSettings}><RotateCcw />Reset playback settings</button>
        </> : settingsPage === 'speed' ? <div className={styles.speeds} role="radiogroup" aria-label="Playback speed">{SPEEDS.map(value => <button key={value} type="button" role="radio" tabIndex={speed === value ? 0 : -1} aria-label={`${value === 1 ? 'Normal' : `${value}×`} speed`} aria-checked={speed === value} onClick={() => changeSpeed(value)}><span>{value === 1 ? 'Normal' : `${value}×`}</span>{speed === value && <Checkmark />}</button>)}</div>
          : <dl className={styles.shortcuts}>{SHORTCUTS.map(([label, keys]) => <div key={label}><dt>{label}</dt><dd><kbd>{keys}</kbd></dd></div>)}</dl>}
      </div>
    </div>}
  </div>;
}
